<script lang="ts">
    import { otto } from '@otto-ec/global-resources/nexus';
    import { onMount } from 'svelte';
    import DynamicEsi from '@shared/components/dynamic-esi/DynamicEsi.svelte';
    import { log } from '@shared/log.browser.ts';
    import type { SdaRenderedEventData } from '@shared/types/Sda';
    import type { SearchContext } from '@shared/types/types.ts';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import { sdaHoardingAttributes } from '@tilelist/domain/external-data-attributes/ExternalDataAttributes.ts';

    interface Props {
        listSlot: SdaHoardingSlot;
        searchContext?: SearchContext;
        isApp?: boolean;
        featureIndex?: number;
        /*                                                                         */
        hidden?: true;
    }

    let { listSlot, searchContext, isApp = false, featureIndex, hidden }: Props = $props();

    /*                                                             */
    const onex = injectWiring().onex;
    const enableHideRendering = $derived(onex.isToggleActive('REPTILE_1642_SDA_HOARDING_HIDE_RENDERING'));

    let shouldRender = $state(true);
    const esiSrcs = $derived(resolveEsiSrcs(listSlot, searchContext, isApp));

    /**
 *
 *
 */
    const SLOT_NAMES = {
        WEB_ES_ATF_NATIVE_BILLBOARD: 'web_es_atf_native-billboard',
        WEB_ES_BTF_NATIVE_BILLBOARD: 'web_es_btf_native-billboard',
        WEB_BTF_MCA_640: 'web_btf_mca_640',
        WEB_BTF_MCA_320: 'web_btf_mca_320',
        APP_BTF_MCA_640: 'app_btf_mca_640',
        APP_BTF_MCA_320: 'app_btf_mca_320',
    } as const;

    export type SdaHoardingSlot = 'top-slot' | 'list-slot' | 'bottom-slot';

    /**
 *
 *
 */
    function getSlotIdentifier(slot: SdaHoardingSlot, isApp = false): string {
        const prefix = isApp ? 'app' : 'web';
        switch (slot) {
            case 'top-slot':
                return SLOT_NAMES.WEB_ES_ATF_NATIVE_BILLBOARD;
            case 'list-slot':
                return SLOT_NAMES.WEB_ES_BTF_NATIVE_BILLBOARD;
            case 'bottom-slot':
                return `${prefix}_btf_mca_640`;
        }
    }

    function normalizeTopNImplicit(topNImplicit?: { [key: string]: string[] }): { [key: string]: string[] } {
        if (!topNImplicit) return {};
        return Object.fromEntries(Object.entries(topNImplicit).map(([key, value]) => [key.toLowerCase(), value]));
    }

    function buildEsiUrl(slotName: string, searchContext?: SearchContext, ssr?: true): string {
        const params = new URLSearchParams();

        if (ssr) {
            params.append('ssr', 'true');
        }

        if (searchContext) {
            const assortmentPath = searchContext.assortment ?? searchContext.categoryPath;
            if (assortmentPath) {
                params.append('assortmentPath', assortmentPath);
            }

            const normalized = normalizeTopNImplicit(searchContext.topNImplicit);
            const appendIfPresent = (key: string, paramName: string) => {
                const value = normalized[key]?.[0];
                if (value) params.append(paramName, value);
            };

            appendIfPresent('brandname', 'brandName');
            appendIfPresent('brandgroupname', 'brandGroupName');
            appendIfPresent('produktbasisklasse', 'produktbasisklasse');
        }

        const query = params.toString();
        return query ? `/sda-hoarding/sda-new/${slotName}?${query}` : `/sda-hoarding/sda-new/${slotName}`;
    }

    function resolveEsiSrcs(slot: SdaHoardingSlot, searchContext?: SearchContext, isApp = false): string[] {
        const prefix = isApp ? 'app' : 'web';
        switch (slot) {
            case 'top-slot':
                return [buildEsiUrl(SLOT_NAMES.WEB_ES_ATF_NATIVE_BILLBOARD, searchContext, true)];
            case 'list-slot':
                return [buildEsiUrl(SLOT_NAMES.WEB_ES_BTF_NATIVE_BILLBOARD, searchContext)];
            case 'bottom-slot':
                return [
                    buildEsiUrl(`${prefix}_btf_mca_640`, searchContext),
                    buildEsiUrl(`${prefix}_btf_mca_320`, searchContext),
                ];
        }
    }

    /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
    onMount(() => {
        if (!enableHideRendering) return;

        /*                                                                              */
        const currentSlotId = getSlotIdentifier(listSlot, isApp);

        log.debug(
            `SDA Hoarding: Subscribing to renderedEvent for currentSlotId="${currentSlotId}", listSlot="${listSlot}"`,
        );

        const unsubscribe = otto.sda.renderedEvent.subscribe(
            ({ rendered, slotIdentifier: receivedSlotId }: SdaRenderedEventData) => {
                log.debug(
                    `SDA renderedEvent: receivedSlotId="${receivedSlotId}", rendered=${rendered}, currentSlotId="${currentSlotId}", listSlot="${listSlot}"`,
                );

                if (receivedSlotId !== currentSlotId) return;
                if (rendered !== false) return;

                log.debug(`SDA slot hiding: slotIdentifier="${receivedSlotId}"`);
                shouldRender = false;
            },
        );

        return () => unsubscribe();
    });
</script>

<div
    class="reptile-sda-hoarding reptile-sda-hoarding--{listSlot}"
    style:display={hidden || !shouldRender ? 'none' : undefined}
    {...sdaHoardingAttributes(featureIndex)}
>
    {#each esiSrcs as src}
        <DynamicEsi {src} name="SdaHoarding" />
    {/each}
</div>

<style lang="scss">
    @use '@otto-ec/otto-components-utils/scss/mixins';

    .reptile-sda-hoarding {
        grid-column: 1 / -1;

        &--top-slot {
            width: 100%;

            @include mixins.breakpoint-from-l() {
                width: auto;
            }
        }

        &--bottom-slot {
            width: 100%;
        }
    }
</style>
